import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

// COMPONENTS
import KogModalComponent from "@/main/components/modal/ModalComponent";
import Button from "@/main/components/shared/button/button";
import KogPaginationShowMore from "@/main/components/shared/kogPaginationShowMore/kogPaginationShowMore";

// MODEL/API/SERVICES
import { quoteItemApi } from "@/main/api";
import { KeyValue } from "@/main/models/common";
import { GetPredictRequest, MapPartRequest } from "@/main/models/request";
import {
	MappingPartDetailViewResponse,
	MappingPartSuggestionViewResponse,
	QuoteItemMappingPartViewReponse,
} from "@/main/models/response";
import { getTranslateByKey, isEmpty, isNullUndefined } from "@/main/services/utils";

// COMPONENTS & STYLES LOCAL
import RowDetailsMappingParts from "./rowDetailsMappingParts";
import RowManualMappingParts from "./rowManualMappingParts";
import SkeletonrowDetailsMappingParts from "./skeletonRowDetailsMappingParts";
import * as S from "./styles";

interface IFormOptionDetails {
	category: KeyValue | null;
	subCategory: KeyValue | null;
	part: KeyValue | null;
	position: KeyValue | null;
	percentage: number | null;
	piesPartId: number | null;
	piesPosId: number | null;
}
const INITIAL_FORM_STATE = {
	guidDetails: "" as string,
	category: null as KeyValue | null,
	subCategory: null as KeyValue | null,
	position: null as KeyValue | null,
	part: null as KeyValue | null,
	partName: "" as string,
};
export type IFormDetailsMappingParts = typeof INITIAL_FORM_STATE;
interface IPropsDetailsMappingPartsProps {
	expanded: boolean;
	mappingParts: QuoteItemMappingPartViewReponse;
	hasLastItem: boolean;
	updateIdRowToOpenCollapse: (row: QuoteItemMappingPartViewReponse, isNextPart: boolean) => void;
}
function DetailsMappingParts({
	expanded,
	mappingParts,
	hasLastItem,
	updateIdRowToOpenCollapse,
}: IPropsDetailsMappingPartsProps) {
	const methods = useForm<IFormDetailsMappingParts>({
		defaultValues: INITIAL_FORM_STATE,
	});

	const [openModalSave, setOpenModalSave] = useState(false);
	const [goNextPaart, setGoNextPart] = useState(false);
	const [optionSelected, setOptionSelected] = useState("");
	const [optionSelectedFull, setOptionSelectedFull] = useState<IFormOptionDetails | null>(null);
	const [dataPartDetails, setDataPartDetails] = useState<MappingPartSuggestionViewResponse[] | null>(null);
	const [dataPartDetailsFull, setDataPartDetailsFull] = useState<MappingPartDetailViewResponse | null>(null);
	const [isLoadingPartDetails, setIsLoadingPartDetails] = useState(false);
	const [isLoadingSave, setIsLoadingSave] = useState(false);

	function fallbackHandleSave() {
		setOpenModalSave(false);
		setGoNextPart(false);
	}

	function handleSave(data: IFormDetailsMappingParts) {
		const messageError = getTranslateByKey("information.somethingWrong", true);
		if (isEmpty(optionSelected))
			return toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.part").toLowerCase(),
				),
			);

		if (optionSelected === "manual") {
			setOptionSelectedFull({
				...data,
				percentage: 0,
				piesPartId: 0,
				piesPosId: 0,
			});
		} else {
			if (isNullUndefined(dataPartDetails)) return toast.error(messageError);

			const findedOption = dataPartDetails.find((item) => item.order === optionSelected);
			if (isEmpty(findedOption)) return toast.error(messageError);

			setOptionSelectedFull({
				category: {
					key: findedOption.guidPartCategory.toString(),
					value: findedOption.partCategoryName,
				},
				subCategory: {
					key: findedOption.guidPartSubCategory.toString(),
					value: findedOption.partSubCategoryName,
				},
				part: {
					key: findedOption.piesPartGuid,
					value: findedOption.xnuupPartId,
				},
				position: {
					key: findedOption.guidPartPosition.toString(),
					value: findedOption.partPositionName,
				},
				percentage: findedOption.confidence * 100,
				piesPartId: findedOption.piesPartId,
				piesPosId: findedOption.piesPosId,
			});
		}

		setOpenModalSave(true);
	}

	async function onClickActionConfirm() {
		setIsLoadingSave(true);
		const request: MapPartRequest = {
			inferences: String(dataPartDetailsFull?.inferences),
			insurerDescription: mappingParts.partDescription,
			partNumber: mappingParts.partNumber,
			piesPartGuid: String(optionSelectedFull?.part?.key),
			piesPosGuid: String(optionSelectedFull?.position?.key),
			guidCategory: String(optionSelectedFull?.category?.key),
			guidSubcategory: String(optionSelectedFull?.subCategory?.key),
			guidQuote: mappingParts.guidQuote,
			caseId: Number(dataPartDetailsFull?.caseId),
		};

		quoteItemApi
			.saveMappingPart(request)
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				updateIdRowToOpenCollapse(mappingParts, goNextPaart);
				if (goNextPaart) {
					setGoNextPart(false);
				}
				setOpenModalSave(false);
			})
			.finally(() => setIsLoadingSave(false));
	}

	async function fetchDataPartDetails() {
		setIsLoadingPartDetails(true);
		const request: GetPredictRequest = {
			partNumber: mappingParts.partNumber,
			insurerDescription: mappingParts.partDescription,
			guidQuote: mappingParts.guidQuote,
		};
		quoteItemApi
			.getMappingPartDetail(request)
			.then((response) => {
				const { result } = response.data;
				setDataPartDetailsFull(result);
				setDataPartDetails(result.data);
			})
			.finally(() => setIsLoadingPartDetails(false));
	}

	useEffect(() => {
		if (expanded) {
			if (isNullUndefined(dataPartDetails)) fetchDataPartDetails();
		}
	}, [expanded]);

	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(handleSave, fallbackHandleSave)} noValidate>
				<S.Container>
					{isLoadingPartDetails ? (
						<SkeletonrowDetailsMappingParts />
					) : (
						<KogPaginationShowMore
							dataList={dataPartDetails}
							render={(item) => (
								<RowDetailsMappingParts
									key={item.order}
									optionSelected={optionSelected}
									setOptionSelected={setOptionSelected}
									detailsOption={item}
								/>
							)}
							renderFinalList={
								<RowManualMappingParts
									mappingParts={mappingParts}
									optionSelected={optionSelected}
									setOptionSelected={setOptionSelected}
								/>
							}
						/>
					)}

					<S.AreaButton>
						<Button id="id-mappingParts-btn-save" type="submit" disabled={isLoadingSave || isLoadingPartDetails}>
							{getTranslateByKey("information.save")}
						</Button>
						{!hasLastItem && (
							<Button
								id="id-mappingParts-btn-saveNextPart"
								type="button"
								disabled={isLoadingSave || isLoadingPartDetails}
								onClick={() => {
									setGoNextPart(true);
									methods.handleSubmit(handleSave, fallbackHandleSave)();
								}}
							>
								{getTranslateByKey("information.saveAndMapNextPart")}
							</Button>
						)}
					</S.AreaButton>
					<KogModalComponent
						title=""
						size="large"
						hasActionConfirm
						hasActionCancel
						open={openModalSave}
						isLoadingActions={isLoadingSave}
						labelActionCancel={getTranslateByKey("information.no")}
						labelActionConfirm={getTranslateByKey("information.yes")}
						onClickActionCancel={() => {
							setOpenModalSave(false);
						}}
						onClickActionConfirm={onClickActionConfirm}
						handleClose={() => {
							setOpenModalSave(false);
						}}
						content={
							<p>
								{getTranslateByKey("information.saveMapPartConfirm")
									.replace("{0}", `${mappingParts.partNumber}`)
									.replace("{1}", `${optionSelectedFull?.category?.value}`)
									.replace("{2}", `${optionSelectedFull?.subCategory?.value} `)
									.replace("{3}", `${optionSelectedFull?.part?.value}`)
									.replace("{4}", `${optionSelectedFull?.position?.value} `)
									.replace("{5}", `${optionSelectedFull?.percentage?.toFixed(2)}%`)
									.replace("{6}", ``)}
							</p>
						}
					/>
				</S.Container>
			</form>
		</FormProvider>
	);
}

export default DetailsMappingParts;
